import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, FormGroup, FormControlLabel, Checkbox,
    Divider, Typography, Grid, TextField, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { tagList } from '../../utils/placeTagsOptions';
import {
    setMarkerTagFilters,
    markerTagFiltersSelector,
    markersSelector,
    markersListSelector,
    pinnedMarkersListSelector,
    NO_TAG_VALUE,
    searchMarkers
} from '../../features/places/placesSlice';

const MarkerTagFilterModal = ({ open, handleClose }) => {
    const dispatch = useDispatch();
    const savedFilters = useSelector(markerTagFiltersSelector);
    const markers = useSelector(markersSelector);
    const markersList = useSelector(markersListSelector);
    const pinnedMarkers = useSelector(pinnedMarkersListSelector);
    const [selectedTags, setSelectedTags] = useState([]);
    const [nameSearch, setNameSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [localSearchResults, setLocalSearchResults] = useState([]);

    // Create an extended tag list that includes the NO_TAG option
    const extendedTagList = useMemo(() => [...tagList, NO_TAG_VALUE], []);

    useEffect(() => {
        if (savedFilters) {
            setSelectedTags(savedFilters);
        }
    }, [savedFilters]);

    // Perform local search to update UI immediately
    useEffect(() => {
        if (!nameSearch.trim() || !markers || !markersList) {
            setLocalSearchResults(markersList);
            return;
        }

        const searchTerm = nameSearch.trim().toUpperCase();
        const results = [...pinnedMarkers];

        markersList.forEach(markerId => {
            const marker = markers[`${markerId}`];
            if (marker && marker.marker_name && marker.marker_name.toUpperCase().search(searchTerm) !== -1) {
                if (results.indexOf(markerId) === -1) {
                    results.push(markerId);
                }
            }
        });

        setLocalSearchResults(results);
    }, [nameSearch, markers, markersList, pinnedMarkers]);

    // Calculate the count of markers for each tag
    const tagCounts = useMemo(() => {
        const counts = {};
        // Initialize all tags with 0
        extendedTagList.forEach(tag => {
            counts[tag] = 0;
        });

        // Only count markers that match the name filter
        const markersListToUse = isSearching ? localSearchResults : markersList;

        // Count markers for each tag
        if (markers && markersListToUse) {
            markersListToUse.forEach(markerId => {
                const marker = markers[`${markerId}`];
                if (marker) {
                    if (!marker.marker_tags || marker.marker_tags.length === 0) {
                        // Count markers with no tags
                        counts[NO_TAG_VALUE] = (counts[NO_TAG_VALUE] || 0) + 1;
                    } else {
                        // Count markers for each tag they have
                        marker.marker_tags.forEach(tag => {
                            counts[tag] = (counts[tag] || 0) + 1;
                        });
                    }
                }
            });
        }
        return counts;
    }, [markers, markersList, localSearchResults, extendedTagList, isSearching]);

    // Count visible markers based on selected tags and name search
    const visibleMarkersCount = useMemo(() => {
        if (!markers) return 0;

        // When searching by name, count the local search results
        if (isSearching) {
            return localSearchResults.length;
        }

        // Regular tag-based filtering when not searching by name
        // If no tags selected, return only pinned markers count
        if (selectedTags.length === 0) return pinnedMarkers.length;

        // If all tags selected, return all markers
        if (selectedTags.length === extendedTagList.length) return markersList.length;

        // Otherwise count markers with matching tags
        let matchedMarkerIds = new Set(pinnedMarkers); // Include pinned markers

        markersList.forEach(markerId => {
            const marker = markers[`${markerId}`];
            if (marker) {
                // Handle markers with no tags
                if (!marker.marker_tags || marker.marker_tags.length === 0) {
                    if (selectedTags.includes(NO_TAG_VALUE)) {
                        matchedMarkerIds.add(markerId);
                    }
                } else {
                    // Check if any marker tag matches selected filters
                    const hasMatchingTag = marker.marker_tags.some(tag => selectedTags.includes(tag));
                    if (hasMatchingTag) {
                        matchedMarkerIds.add(markerId);
                    }
                }
            }
        });

        return matchedMarkerIds.size;
    }, [markers, markersList, selectedTags, pinnedMarkers, extendedTagList, localSearchResults, isSearching]);

    const handleTagToggle = (tag) => {
        // Don't allow tag toggling when in search mode
        if (isSearching) return;

        setSelectedTags(prevTags => {
            if (prevTags.includes(tag)) {
                return prevTags.filter(t => t !== tag);
            } else {
                return [...prevTags, tag];
            }
        });
    };

    const handleSelectAll = () => {
        // Don't allow selection changes when in search mode
        if (isSearching) return;

        setSelectedTags([...extendedTagList]);
    };

    const handleSelectNone = () => {
        // Don't allow selection changes when in search mode
        if (isSearching) return;

        setSelectedTags([]);
    };

    const handleNameSearchChange = (event) => {
        const value = event.target.value;
        setNameSearch(value);
        setIsSearching(value.trim() !== '');
    };

    const handleClearSearch = () => {
        setNameSearch('');
        setIsSearching(false);
    };

    const handleApply = () => {
        if (isSearching) {
            // Directly dispatching the Redux searchMarkers action
            dispatch(searchMarkers(nameSearch));
        } else {
            // When filtering by tags, apply the tag filters
            dispatch(setMarkerTagFilters(selectedTags));
        }
        handleClose();
    };

    // Split tags into two columns, including the NO_TAG option in the second column
    const regularTagList = [...tagList];
    const leftColumnTags = regularTagList.slice(0, Math.ceil(regularTagList.length / 2));
    const rightColumnTags = regularTagList.slice(Math.ceil(regularTagList.length / 2));

    // Tag label with count component
    const TagLabel = ({ tag, count }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <Typography variant="body2">
                {tag}
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    backgroundColor: selectedTags.includes(tag) ? '#e3f2fd' : '#eee',
                    color: selectedTags.includes(tag) ? '#1976d2' : '#666',
                    borderRadius: '10px',
                    padding: '2px 8px',
                    fontWeight: 500,
                    minWidth: '32px',
                    textAlign: 'center',
                    marginLeft: '8px'
                }}
            >
                {count}
            </Typography>
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                padding: '16px 24px',
                backgroundColor: '#f9f9f9'
            }}>
                <Typography variant="h6" component="div">
                    Filter Markers by Tag
                </Typography>
                <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{
                        color: 'text.secondary',
                        backgroundColor: '#eee',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '0.9rem',
                        fontWeight: 500
                    }}
                >
                    {visibleMarkersCount} markers
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ padding: '24px' }}>
                {/* Name search field */}
                <TextField
                    fullWidth
                    placeholder="Search markers by name"
                    value={nameSearch}
                    onChange={handleNameSearchChange}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: nameSearch && (
                            <InputAdornment position="end">
                                <Button
                                    onClick={handleClearSearch}
                                    size="small"
                                    sx={{ minWidth: 'auto', p: '2px' }}
                                >
                                    Clear
                                </Button>
                            </InputAdornment>
                        ),
                    }}
                />

                {isSearching && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mb: 2,
                            mt: -1,
                            fontStyle: 'italic',
                            p: 1,
                            backgroundColor: '#fff9c4',
                            borderRadius: '4px'
                        }}
                    >
                        Searching by name. Tag filters are disabled in search mode.
                    </Typography>
                )}

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                    marginTop: '16px'
                }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectAll}
                        sx={{
                            borderRadius: '4px',
                            minWidth: '110px'
                        }}
                        disabled={isSearching}
                    >
                        Select All
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectNone}
                        sx={{
                            borderRadius: '4px',
                            minWidth: '110px'
                        }}
                        disabled={isSearching}
                    >
                        Select None
                    </Button>
                </div>
                <Divider style={{ marginBottom: '16px' }} />

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <FormGroup>
                            {leftColumnTags.map((tag) => (
                                <FormControlLabel
                                    key={tag}
                                    control={
                                        <Checkbox
                                            checked={isSearching || selectedTags.includes(tag)}
                                            onChange={() => handleTagToggle(tag)}
                                            color="primary"
                                            size="small"
                                            disabled={isSearching}
                                        />
                                    }
                                    label={<TagLabel tag={tag} count={tagCounts[tag] || 0} />}
                                    sx={{
                                        margin: '2px 0',
                                        opacity: isSearching ? 0.7 : 1,
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.9rem',
                                            width: '100%'
                                        }
                                    }}
                                />
                            ))}
                        </FormGroup>
                    </Grid>
                    <Grid item xs={6}>
                        <FormGroup>
                            {rightColumnTags.map((tag) => (
                                <FormControlLabel
                                    key={tag}
                                    control={
                                        <Checkbox
                                            checked={isSearching || selectedTags.includes(tag)}
                                            onChange={() => handleTagToggle(tag)}
                                            color="primary"
                                            size="small"
                                            disabled={isSearching}
                                        />
                                    }
                                    label={<TagLabel tag={tag} count={tagCounts[tag] || 0} />}
                                    sx={{
                                        margin: '2px 0',
                                        opacity: isSearching ? 0.7 : 1,
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.9rem',
                                            width: '100%'
                                        }
                                    }}
                                />
                            ))}

                            {/* Special "No Tag" option */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isSearching || selectedTags.includes(NO_TAG_VALUE)}
                                        onChange={() => handleTagToggle(NO_TAG_VALUE)}
                                        color="primary"
                                        size="small"
                                        disabled={isSearching}
                                    />
                                }
                                label={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                color: '#666',
                                                fontStyle: 'italic'
                                            }}
                                        >
                                            No Tag
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                backgroundColor: selectedTags.includes(NO_TAG_VALUE) ? '#e3f2fd' : '#eee',
                                                color: selectedTags.includes(NO_TAG_VALUE) ? '#1976d2' : '#666',
                                                borderRadius: '10px',
                                                padding: '2px 8px',
                                                fontWeight: 500,
                                                minWidth: '32px',
                                                textAlign: 'center',
                                                marginLeft: '8px',
                                                fontStyle: 'normal'
                                            }}
                                        >
                                            {tagCounts[NO_TAG_VALUE] || 0}
                                        </Typography>
                                    </div>
                                }
                                sx={{
                                    margin: '2px 0',
                                    marginTop: '8px',
                                    borderTop: '1px dashed #eee',
                                    paddingTop: '8px',
                                    opacity: isSearching ? 0.7 : 1,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.9rem',
                                        width: '100%'
                                    }
                                }}
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{
                padding: '16px 24px',
                borderTop: '1px solid rgba(0,0,0,0.1)',
                backgroundColor: '#f9f9f9'
            }}>
                <Button
                    onClick={handleClose}
                    color="primary"
                    sx={{ borderRadius: '4px' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleApply}
                    color="primary"
                    variant="contained"
                    sx={{
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarkerTagFilterModal; 